import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import {
  QuizModeProvider,
  QuizSession,
  QuizStyleProvider,
  type QuizResponseData,
  type QuizResponseResult,
  type QuizScript,
} from '@quebon/shared-learning'
import { quizScriptApi } from '@/api/quizScript'
import { getSpecialLectureDetail, isUnitAssessmentNotFound, saveOnlineAnswer, submitOnline } from '@/api/unitAssessment'
import { activeSpecialLectureKey } from '@/hooks/learner/useActiveSpecialLecture'
import { Button } from '@/components/learner/ui/Button'
import { SpecialLectureStartCard } from './SpecialLectureStartCard'
import { SpecialLectureLoading, SpecialLecturePageState } from './SpecialLecturePageState'

/** 단원평가 특강 온라인 풀이 — 리뷰 전 정오답/해설 비노출(assessmentMode). */
export default function SpecialLectureOnline() {
  const { id = '' } = useParams<{ id: string }>()
  const numericId = useMemo(() => Number(id), [id])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { t } = useTranslation('learner')

  const [quizList, setQuizList] = useState<QuizScript[] | null>(null)
  const [initialIndex, setInitialIndex] = useState(0)
  const [setName, setSetName] = useState('')
  const [started, setStarted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadAttempt, setLoadAttempt] = useState(0)

  useEffect(() => {
    let active = true
    ;(async () => {
      setError(null)
      try {
        const detail = await getSpecialLectureDetail(numericId)
        const quizIds = detail.items.map((it) => it.quizId)
        if (quizIds.length === 0) {
          if (active) setError(t('specialLecture.error.empty'))
          return
        }
        const fetched = await quizScriptApi.getQuizzes(quizIds, false, true, false)
        const byId = new Map(fetched.map((q) => [String(q.quizId), q]))
        const ordered = quizIds.map((qid) => byId.get(String(qid))).filter(Boolean) as QuizScript[]
        if (!active) return
        if (ordered.length === 0) {
          setError(t('specialLecture.error.load'))
          return
        }
        setSetName(detail.setName)
        setInitialIndex(Math.min(detail.resumeFromIndex, ordered.length - 1))
        setQuizList(ordered)
      } catch (e) {
        if (isUnitAssessmentNotFound(e)) {
          navigate('/my', { replace: true })
          return
        }
        if (active) setError(t('specialLecture.error.load'))
      }
    })()
    return () => {
      active = false
    }
  }, [loadAttempt, navigate, numericId, t])

  const onQuizResponse = useCallback(
    async (data: QuizResponseData): Promise<QuizResponseResult> => {
      await saveOnlineAnswer(numericId, data.quizId, data.answers)
      // 리뷰 전이므로 정오답 비노출(엣지6) — assessmentMode 가 결과를 숨긴다.
      return { isCorrect: false, corrects: [], correctAnswers: [] }
    },
    [numericId],
  )

  const onComplete = useCallback(async () => {
    await submitOnline(numericId)
    await queryClient.invalidateQueries({ queryKey: activeSpecialLectureKey })
    navigate(`/my/special-lecture/${numericId}/wait`, { replace: true })
  }, [navigate, numericId, queryClient])

  if (error) {
    return (
      <SpecialLecturePageState
        kind={error === t('specialLecture.error.empty') ? 'empty' : 'error'}
        title={error === t('specialLecture.error.empty')
          ? t('specialLecture.state.emptyTitle')
          : t('specialLecture.state.errorTitle')}
        description={error}
        actionLabel={t('specialLecture.state.retry')}
        onAction={() => {
          setQuizList(null)
          setLoadAttempt((attempt) => attempt + 1)
        }}
      />
    )
  }
  if (!quizList) {
    return <SpecialLectureLoading />
  }
  if (!started) {
    return (
      <SpecialLectureStartCard
        setName={setName}
        title={t('specialLecture.start.title')}
        description={t('specialLecture.start.description')}
        count={quizList.length}
        countLabel={t('specialLecture.start.count', { total: quizList.length })}
        resumeLabel={initialIndex > 0 ? t('specialLecture.start.resume', { solved: initialIndex }) : undefined}
        completedCount={initialIndex > 0 ? initialIndex : undefined}
        action={(
          <Button size="XL" className="w-full px-10 sm:w-auto" onClick={() => setStarted(true)}>
            {initialIndex > 0 ? t('specialLecture.start.ctaResume') : t('specialLecture.start.cta')}
          </Button>
        )}
      />
    )
  }
  return (
    <QuizModeProvider value={{ mode: 'student' }}>
      <QuizStyleProvider variant="lc">
        <QuizSession
          contentId={0}
          quizList={quizList}
          quizScriptApi={quizScriptApi}
          mode="student"
          assessmentMode
          assessmentActionPlacement="card-end"
          sessionId={numericId}
          initialIndex={initialIndex}
          onQuizResponse={onQuizResponse}
          onComplete={onComplete}
        />
      </QuizStyleProvider>
    </QuizModeProvider>
  )
}
